// ─────────────────────────────────────────────────────────────────────────────
// V2InvoiceView — the on-screen invoice opened from Invoices (v2).
// This is the *screen* rendering only. The downloadable PDF is unchanged: "Download PDF"
// calls the same server endpoint as v1 (GET /api/pdf/generate/:id).
// Data: GET /api/invoices/:id (full doc incl. payments, populated client + salesperson).
// Actions are delegated to V2Invoices' existing sheets (record payment, IRN, cancel) via props;
// IRN cancel and delete-cancelled are handled here with confirmation sheets.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import userContext from "../context/user/userContext";
import bankContext from "../context/bank/bankContext";
import API_BASE from "../apiConfig";
import { Glyph, Duo } from "./icons";
import { MenuButton, Ring, Sheet, Skel, inr, useMedia } from "./primitives";
import { sendInvoiceViaWhatsApp, sendPaymentReminderViaWhatsApp } from "../utils/whatsapp";
import { Field } from "./kit";

const auth = () => ({ "auth-token": localStorage.getItem("token") });
const json = () => ({ "Content-Type": "application/json", ...auth() });
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const money = (v) => `₹${r2(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");
const fmtDT = (d) => (d ? new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
const DOC = { TAX_INVOICE: "Tax Invoice", DELIVERY_CHALLAN: "Delivery Challan", CREDIT_NOTE: "Credit Note", DEBIT_NOTE: "Debit Note" };
// Same terms v1 shows on screen (content unchanged; only presentation differs).
const TERMS = {
  goods: ["Goods once sold are not returnable.", "Our responsibility ceases after delivery.", "Subject to local jurisdiction only."],
  service: ["Payment due within 30 days of invoice date.", "Late payments attract 1.5% interest per month.", "Subject to local jurisdiction only."],
};

// Indian-system amount in words: "Twelve Thousand Five Hundred Rupees and Fifty Paise Only"
const ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
const two = (n) => (n < 20 ? ONES[n] : `${TENS[Math.floor(n / 10)]}${n % 10 ? ` ${ONES[n % 10]}` : ""}`);
const three = (n) => `${n >= 100 ? `${ONES[Math.floor(n / 100)]} Hundred${n % 100 ? " " : ""}` : ""}${n % 100 ? two(n % 100) : ""}`;
function intWords(n) {
  if (!n) return "";
  const parts = [];
  const crore = Math.floor(n / 1e7), lakh = Math.floor(n / 1e5) % 100, thou = Math.floor(n / 1e3) % 100, rest = n % 1000;
  if (crore) parts.push(`${intWords(crore)} Crore`);
  if (lakh) parts.push(`${two(lakh)} Lakh`);
  if (thou) parts.push(`${two(thou)} Thousand`);
  if (rest) parts.push(three(rest));
  return parts.join(" ");
}
function inWords(amount) {
  const total = Math.round((Number(amount) || 0) * 100);
  const rupees = Math.floor(total / 100), paise = total % 100;
  if (!rupees && !paise) return "Zero Rupees Only";
  const r = rupees ? `${intWords(rupees)} Rupees` : "";
  const p = paise ? `${two(paise)} Paise` : "";
  return `${[r, p].filter(Boolean).join(" and ")} Only`;
}

export function computeInvoice(inv) {
  const items = inv.items || [];
  const lineAmt = (it) => (it.unitPrice || it.price || 0) * (it.qty || 0);
  const subtotal = inv.subtotal ?? items.reduce((a, it) => a + lineAmt(it), 0);
  const discount = inv.discountAmount || 0;
  const cgst = inv.totalCgst || 0, sgst = inv.totalSgst || 0, igst = inv.totalIgst || 0;
  const charges = [["Freight", inv.freight], ["Insurance", inv.insurance], ["Packaging", inv.packagingCharges], ["Loading", inv.loadingCharges], [inv.otherChargesDesc || "Other charges", inv.otherCharges]]
    .filter(([, v]) => Number(v) > 0);
  const chargesTotal = charges.reduce((a, [, v]) => a + Number(v), 0);
  const inclusive = inv.gstType === "Inclusive";
  const pre = inclusive ? subtotal - discount + chargesTotal : subtotal - discount + cgst + sgst + igst + chargesTotal;
  const roundOff = r2(inv.grandTotal - pre);
  const byRate = {};
  items.forEach((it) => { const k = it.gstRate || 0; byRate[k] = byRate[k] || { rate: k, taxable: 0, tax: 0 }; byRate[k].taxable += lineAmt(it); byRate[k].tax += (it.cgstAmount || 0) + (it.sgstAmount || 0) + (it.igstAmount || 0); });
  const paid = inv.amountPaid || 0;
  const due = Math.max(0, r2(inv.grandTotal - paid));
  const dueDate = new Date(new Date(inv.invoiceDate).getTime() + (inv.paymentTimeline ?? 30) * 864e5);
  const overdueDays = inv.paymentStatus !== "PAID" && inv.status === "ACTIVE" ? Math.floor((Date.now() - dueDate) / 864e5) : 0;
  return { items, lineAmt, subtotal, discount, cgst, sgst, igst, charges, inclusive, roundOff, rates: Object.values(byRate).sort((a, b) => a.rate - b.rate), paid, due, dueDate, overdueDays };
}

// The invoice document itself — shared by the in-app view and the public invoice page.
// seller: { companyName, companyAddress, companyGSTIN, companyPhone|phone, companyEmail }
export function InvoicePaper({ inv, calc, seller, bank, isPhone }) {
  const user = seller;
  const client = inv.clientId && typeof inv.clientId === "object" ? inv.clientId : {};
  const clientName = inv.clientName || client.clientName || "—";
  const active = inv.status === "ACTIVE";
  const isGoods = inv.invoiceType === "goods";
  const upi = bank?.upiId && active && calc.due > 0 ? `upi://pay?pa=${bank.upiId}&pn=${encodeURIComponent(user?.companyName || "")}&am=${calc.due.toFixed(2)}&cu=INR&tn=${encodeURIComponent(inv.invoiceNumber)}` : null;
  const ship = inv.shipTo && (inv.shipTo.name || inv.shipTo.address) ? inv.shipTo : null;
  const logistics = [["Buyer's order", inv.buyerOrderNo], ["Reference", inv.referenceNo], ["Dispatch doc", inv.dispatchDocNo], ["Dispatched via", inv.dispatchedThrough || inv.transporterName], ["Vehicle", inv.vehicleNo], ["E-way bill", inv.eWayBillNo ? `${inv.eWayBillNo}${inv.eWayBillValidTill ? ` · till ${fmtDate(inv.eWayBillValidTill)}` : ""}` : ""]].filter(([, v]) => v);
  return (
    <article className={`v2iv-paper${!active ? " is-void" : ""}`} id="v2-invoice-paper">
      {!active && <div className="v2iv-void" aria-hidden="true">Cancelled</div>}
      <div className="v2iv-top">
        <div className="v2iv-seller">
          <span className="v2iv-mark">{(user?.companyName || "?").trim().slice(0, 1).toUpperCase()}</span>
          <div>
            <strong>{user?.companyName || "Your company"}</strong>
            {user?.companyAddress && <p>{user.companyAddress}</p>}
            <p className="v2iv-small">{[user?.companyGSTIN && `GSTIN ${user.companyGSTIN}`, user?.companyPhone || user?.phone, user?.companyEmail].filter(Boolean).join("  ·  ")}</p>
          </div>
        </div>
        <div className="v2iv-doc">
          <span className="v2iv-doc-type">{DOC[inv.documentType] || "Tax Invoice"}</span>
          <strong>{inv.invoiceNumber}</strong>
          <dl>
            <div><dt>Issued</dt><dd>{fmtDate(inv.invoiceDate)}</dd></div>
            <div><dt>Due</dt><dd>{fmtDate(calc.dueDate)}</dd></div>
            {inv.salesperson?.name && <div><dt>Sales</dt><dd>{inv.salesperson.name}</dd></div>}
          </dl>
        </div>
      </div>

      <div className="v2iv-parties">
        <div>
          <span className="v2iv-label">Bill to</span>
          <strong>{clientName}</strong>
          {(inv.clientAddress || client.clientAddress) && <p>{inv.clientAddress || client.clientAddress}</p>}
          {(inv.clientGSTIN || client.clientGSTIN) && <p className="v2iv-small v2iv-mono">GSTIN {inv.clientGSTIN || client.clientGSTIN}</p>}
        </div>
        {ship && <div>
          <span className="v2iv-label">Ship to</span>
          <strong>{ship.name || clientName}</strong>
          <p>{[ship.address, ship.city, ship.state, ship.pincode].filter(Boolean).join(", ")}</p>
          {ship.gstin && <p className="v2iv-small v2iv-mono">GSTIN {ship.gstin}</p>}
        </div>}
        <div className="v2iv-amountbox">
          <span className="v2iv-label">{active && calc.due > 0 ? "Amount due" : "Invoice total"}</span>
          <strong>{money(active && calc.due > 0 ? calc.due : inv.grandTotal)}</strong>
          <span className="v2iv-small">{active && calc.due > 0 ? `by ${fmtDate(calc.dueDate)}` : inv.paymentStatus === "PAID" ? "Paid in full" : ""}</span>
        </div>
      </div>

      {logistics.length > 0 && <dl className="v2iv-logistics">{logistics.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>}

      <table className="v2iv-items">
        <thead><tr><th>#</th><th>Item</th><th className="is-num">Qty</th><th className="is-num">Rate</th><th className="is-num">GST</th><th className="is-num">Amount</th></tr></thead>
        <tbody>{calc.items.map((it, i) => (
          <tr key={i}>
            <td className="v2iv-idx">{i + 1}</td>
            <td><span className="v2iv-item">{it.name}</span>
              <span className="v2iv-item-sub">{[(it.hsnCode || it.hsnSacCode) && `${isGoods ? "HSN" : "SAC"} ${it.hsnCode || it.hsnSacCode}`, isGoods && it.batchNo && `Batch ${it.batchNo}`, isGoods && it.expiryDate && `Exp ${fmtDate(it.expiryDate)}`].filter(Boolean).join(" · ")}</span>
              {isPhone && <span className="v2iv-item-sub">{it.qty} {it.unit || "pcs"} × {money(it.unitPrice || it.price)} · GST {it.gstRate || 0}%</span>}</td>
            <td className="is-num">{it.qty} <span className="v2iv-unit">{it.unit || "pcs"}</span></td>
            <td className="is-num">{money(it.unitPrice || it.price)}</td>
            <td className="is-num">{it.gstRate || 0}%</td>
            <td className="is-num v2iv-amt">{money(calc.lineAmt(it))}</td>
          </tr>))}</tbody>
      </table>

      <div className="v2iv-bottom">
        <div className="v2iv-left">
          <div><span className="v2iv-label">Amount in words</span><p className="v2iv-words">{inWords(inv.grandTotal)}</p></div>
          {calc.rates.length > 0 && (calc.cgst + calc.sgst + calc.igst) > 0 && (
            <table className="v2iv-taxes">
              <thead><tr><th>Rate</th><th className="is-num">Taxable</th>{calc.igst > 0 ? <th className="is-num">IGST</th> : <><th className="is-num">CGST</th><th className="is-num">SGST</th></>}</tr></thead>
              <tbody>{calc.rates.filter((r) => r.rate > 0).map((r) => <tr key={r.rate}><td>{r.rate}%</td><td className="is-num">{money(r.taxable)}</td>
                {calc.igst > 0 ? <td className="is-num">{money(r.tax)}</td> : <><td className="is-num">{money(r.tax / 2)}</td><td className="is-num">{money(r.tax / 2)}</td></>}</tr>)}</tbody>
            </table>)}
          {bank?.accountNumber && (
            <div className="v2iv-pay">
              <div>
                <span className="v2iv-label">Pay to</span>
                <p><b>{bank.bankName}</b>{bank.branchName ? `, ${bank.branchName}` : ""}</p>
                <p className="v2iv-mono">A/c {bank.accountNumber} · IFSC {bank.ifscCode}</p>
                {bank.upiId && <p className="v2iv-small">UPI {bank.upiId}</p>}
              </div>
              {upi && <div className="v2iv-qr"><QRCodeCanvas value={upi} size={84} level="M" /><span>Scan to pay {inr(calc.due)}</span></div>}
            </div>)}
        </div>
        <div className="v2iv-totals">
          <div><span>Subtotal</span><b>{money(calc.subtotal)}</b></div>
          {calc.discount > 0 && <div className="is-neg"><span>Discount{inv.discountPercent ? ` (${inv.discountPercent}%)` : ""}</span><b>−{money(calc.discount)}</b></div>}
          {calc.charges.map(([k, v]) => <div key={k}><span>{k}</span><b>{money(v)}</b></div>)}
          {calc.igst > 0 && <div><span>IGST</span><b>{money(calc.igst)}</b></div>}
          {calc.cgst > 0 && <div><span>CGST</span><b>{money(calc.cgst)}</b></div>}
          {calc.sgst > 0 && <div><span>SGST</span><b>{money(calc.sgst)}</b></div>}
          {calc.inclusive && <div className="is-muted"><span>Prices include GST</span><b /></div>}
          {Math.abs(calc.roundOff) >= 0.01 && Math.abs(calc.roundOff) < 1 && <div className="is-muted"><span>Round off</span><b>{calc.roundOff > 0 ? "+" : "−"}{money(Math.abs(calc.roundOff))}</b></div>}
          <div className="v2iv-grand"><span>Total</span><b>{money(inv.grandTotal)}</b></div>
          {calc.paid > 0 && <>
            <div className="is-good"><span>Paid</span><b>−{money(calc.paid)}</b></div>
            <div className="v2iv-balance"><span>Balance due</span><b>{money(calc.due)}</b></div>
          </>}
        </div>
      </div>

      {inv.irnStatus === "generated" && inv.irn && (
        <div className="v2iv-irn">
          {inv.qrCode && <QRCodeCanvas value={inv.qrCode} size={92} level="M" />}
          <div>
            <span className="v2iv-label">e-Invoice</span>
            <p className="v2iv-mono v2iv-irn-hash">IRN {inv.irn}</p>
            <p className="v2iv-small">Ack {inv.ackNo || "—"} · {fmtDT(inv.ackDate)}</p>
          </div>
        </div>)}

      <footer className="v2iv-foot">
        <ul>{(isGoods ? TERMS.goods : TERMS.service).map((t) => <li key={t}>{t}</li>)}</ul>
        <div className="v2iv-sign"><span>For {user?.companyName || "the company"}</span><i /><span>Authorised signatory</span></div>
      </footer>
    </article>
  );
}

export default function V2InvoiceView({ invoiceId, version, canWrite, onBack, onPay, onConfirm, onDownload, downloading, say }) {
  const { user } = useContext(userContext);
  const { bank, getBankDetails } = useContext(bankContext);
  const isPhone = useMedia("(max-width: 760px)");
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState("");
  const [irnCancel, setIrnCancel] = useState(false);
  const [del, setDel] = useState(null);

  const load = useCallback(async () => {
    setErr("");
    try {
      const r = await fetch(`${API_BASE}/api/invoices/${invoiceId}`, { headers: auth() });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Couldn't open this invoice");
      setInv(d);
    } catch (e) { setErr(e.message); }
  }, [invoiceId]);
  useEffect(() => { load(); }, [load, version]);
  // bankContext starts as an object of empty strings (never null) — fetch unless details are already loaded.
  useEffect(() => { if (!bank?.accountNumber) getBankDetails?.(); }, []); // eslint-disable-line

  const calc = useMemo(() => (inv ? computeInvoice(inv) : null), [inv]);

  const print = () => {
    document.documentElement.setAttribute("data-v2-print", "invoice");
    const done = () => { document.documentElement.removeAttribute("data-v2-print"); window.removeEventListener("afterprint", done); };
    window.addEventListener("afterprint", done);
    window.print();
  };

  if (err) return (
    <div className="v2p v2iv"><button type="button" className="v2l-back" onClick={onBack}><Glyph name="chevronLeft" size={18} stroke={2} /> Invoices</button>
      <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {err}</div></div>);
  if (!inv || !calc) return (
    <div className="v2p v2iv"><button type="button" className="v2l-back" onClick={onBack}><Glyph name="chevronLeft" size={18} stroke={2} /> Invoices</button>
      <div className="v2iv-grid"><Skel h={720} r={22} /><div className="v2iv-side"><Skel h={210} r={26} /><Skel h={160} r={26} /></div></div></div>);

  const client = inv.clientId && typeof inv.clientId === "object" ? inv.clientId : {};
  const clientName = inv.clientName || client.clientName || "—";
  const phone = client.clientPhone;
  const active = inv.status === "ACTIVE";
  const isGoods = inv.invoiceType === "goods";
  const pStatus = !active ? ["Cancelled", "neutral"] : inv.paymentStatus === "PAID" ? ["Paid", "good"] : calc.overdueDays > 0 ? [`Overdue ${calc.overdueDays}d`, "bad"] : inv.paymentStatus === "PARTIAL" ? ["Part-paid", "warn"] : ["Unpaid", "warn"];
  const irnWindow = inv.irnStatus === "generated" && inv.ackDate && Date.now() - new Date(inv.ackDate).getTime() < 864e5;
  const pct = inv.grandTotal ? Math.min(100, (calc.paid / inv.grandTotal) * 100) : 0;

  const more = [
    { label: "Print", glyph: "invoice", onSelect: print },
    ...(phone ? ["divider", { label: "Send on WhatsApp", glyph: "phone", onSelect: () => sendInvoiceViaWhatsApp(inv, phone) },
      ...(active && inv.paymentStatus !== "PAID" ? [{ label: "Payment reminder", glyph: "bell", onSelect: () => sendPaymentReminderViaWhatsApp(inv, phone, clientName) }] : [])] : []),
    ...(canWrite && active ? ["divider",
      ...(inv.irnStatus !== "generated" ? [{ label: "Generate IRN", glyph: "gst", onSelect: () => onConfirm({ kind: "irn", inv }) }] : []),
      ...(irnWindow ? [{ label: "Cancel IRN", glyph: "gst", onSelect: () => setIrnCancel(true) }] : []),
      { label: "Cancel invoice", glyph: "close", danger: true, onSelect: () => onConfirm({ kind: "cancel", inv }) }] : []),
    ...(canWrite && !active ? ["divider", { label: "Delete permanently", glyph: "close", danger: true, onSelect: () => setDel({}) }] : []),
  ];

  return (
    <div className="v2p v2iv">
      <button type="button" className="v2l-back" onClick={onBack}><Glyph name="chevronLeft" size={18} stroke={2} /> Invoices</button>
      <header className="v2iv-head">
        <div className="v2iv-head-main">
          <div className="v2iv-head-title"><h1>{inv.invoiceNumber}</h1><span className={`v2-status v2-status--${pStatus[1]}`}>{pStatus[0]}</span>
            {inv.irnStatus === "generated" && <span className="v2-status v2-status--info"><Glyph name="check" size={12} stroke={2.4} /> e-Invoice</span>}</div>
          <span className="v2iv-head-sub">{clientName} · {fmtDate(inv.invoiceDate)} · {money(inv.grandTotal)}</span>
        </div>
        <div className="v2iv-head-actions">
          {canWrite && active && inv.paymentStatus !== "PAID" && <button type="button" data-kbd="p" title="Record payment (P)" className="v2-btn v2-btn--primary" onClick={() => onPay(inv)}><Glyph name="cash" size={16} /> Record payment</button>}
          <button type="button" data-kbd="d" title="Download PDF (D)" className="v2-btn" disabled={downloading} onClick={() => onDownload(inv)}><Glyph name="download" size={16} /> {downloading ? "Preparing…" : isPhone ? "PDF" : "Download PDF"}</button>
          <MenuButton className="v2-icon-btn v2-glass" label="More actions" width={240} items={more} />
        </div>
      </header>

      {inv.irnStatus !== "generated" && inv.irnErrDetails && active && (
        <div className="v2-banner v2-banner--bad v2iv-banner"><Glyph name="alert" size={16} /> <span><b>Last IRN attempt failed:</b> {inv.irnErrDetails}</span></div>)}

      <div className="v2iv-grid">
        {/* ── The document ─────────────────────────────────────────── */}
        <InvoicePaper inv={inv} calc={calc} seller={user} bank={bank} isPhone={isPhone} />

        {/* ── Side panel ───────────────────────────────────────────── */}
        <aside className="v2iv-side">
          <section className="v2-card v2iv-card">
            <div className="v2iv-paid">
              <Ring value={pct} size={88} stroke={9} color={pct >= 100 ? "var(--v2-good)" : "var(--v2-accent)"} label={`${Math.round(pct)}% paid`}>
                <strong>{Math.round(pct)}%</strong><span>paid</span>
              </Ring>
              <div>
                <span className="v2iv-label">{!active ? "Cancelled" : calc.due > 0 ? "Outstanding" : "Settled"}</span>
                <strong className="v2iv-due">{money(active ? calc.due : 0)}</strong>
                <span className="v2iv-small">{!active ? `Was ${money(inv.grandTotal)}` : calc.due > 0 ? (calc.overdueDays > 0 ? `${calc.overdueDays} days overdue` : `Due ${fmtDate(calc.dueDate)}`) : `of ${money(inv.grandTotal)}`}</span>
              </div>
            </div>
            {canWrite && active && calc.due > 0 && <button type="button" className="v2-btn v2-btn--primary v2iv-wide" onClick={() => onPay(inv)}><Glyph name="cash" size={16} /> Record payment</button>}
            {phone && active && calc.due > 0 && <button type="button" className="v2-btn v2iv-wide" onClick={() => sendPaymentReminderViaWhatsApp(inv, phone, clientName)}><Glyph name="bell" size={16} /> Send reminder</button>}
          </section>

          <section className="v2-card v2iv-card">
            <h3>Payments</h3>
            {(inv.payments || []).length === 0 ? <p className="v2iv-empty">{active ? "No payments recorded yet." : "No payments."}</p>
              : <ol className="v2iv-timeline">{[...inv.payments].sort((a, b) => new Date(b.date) - new Date(a.date)).map((p, i) => (
                <li key={p._id || i}><i /><div><b>{money(p.amount)}</b><span>{fmtDate(p.date)} · {p.paymentMode || "Payment"}{p.reference ? ` · ${p.reference}` : ""}</span>{p.note && <em>{p.note}</em>}</div></li>))}</ol>}
          </section>

          <section className="v2-card v2iv-card">
            <h3>e-Invoice</h3>
            {inv.irnStatus === "generated" ? <>
              <div className="v2iv-irn-row"><Duo name="gst" size={30} /><div><b>IRN generated</b><span>Ack {inv.ackNo} · {fmtDT(inv.ackDate)}</span></div></div>
              <button type="button" className="v2k-link" onClick={() => { navigator.clipboard?.writeText(inv.irn); say?.("IRN copied"); }}>Copy IRN</button>
              {irnWindow && canWrite && <button type="button" className="v2-btn v2-btn--sm v2iv-wide" onClick={() => setIrnCancel(true)}>Cancel IRN <span className="v2iv-small">· within 24 h of ack</span></button>}
            </> : inv.irnStatus === "cancelled" ? <p className="v2iv-empty">IRN cancelled {inv.irnCancelledAt ? fmtDate(inv.irnCancelledAt) : ""}{inv.irnCancelReason ? ` — ${inv.irnCancelReason}` : ""}.</p>
              : <>
                <p className="v2iv-empty">{active ? "Not submitted to the IRP." : "Not applicable — invoice cancelled."}</p>
                {canWrite && active && <button type="button" className="v2-btn v2-btn--sm v2iv-wide" onClick={() => onConfirm({ kind: "irn", inv })}><Glyph name="gst" size={15} /> Generate IRN</button>}
              </>}
          </section>

          <section className="v2-card v2iv-card v2iv-facts">
            <h3>Details</h3>
            <dl>
              <div><dt>Type</dt><dd>{isGoods ? "Goods" : "Services"} · {inv.gstType === "Inclusive" ? "GST inclusive" : "GST exclusive"}</dd></div>
              <div><dt>Tax</dt><dd>{calc.igst > 0 ? "Inter-state (IGST)" : "Intra-state (CGST + SGST)"}</dd></div>
              {inv.supplyType && <div><dt>Supply</dt><dd>{inv.supplyType}</dd></div>}
              {client.clientState && <div><dt>Place of supply</dt><dd>{client.clientState}</dd></div>}
              {inv.salesperson?.name && <div><dt>Salesperson</dt><dd>{inv.salesperson.name}</dd></div>}
              <div><dt>Created</dt><dd>{fmtDT(inv.createdAt || inv.invoiceDate)}</dd></div>
            </dl>
          </section>
        </aside>
      </div>

      <IrnCancelSheet open={irnCancel} inv={inv} onClose={() => setIrnCancel(false)} onDone={() => { setIrnCancel(false); say?.("IRN cancelled"); load(); }} />
      <Sheet open={!!del} onClose={() => setDel(null)} width={440} title={`Delete ${inv.invoiceNumber} permanently?`}
        footer={<><button type="button" className="v2-btn" onClick={() => setDel(null)}>Keep</button>
          <button type="button" className="v2-btn v2c-btn-danger" onClick={async () => {
            try { const r = await fetch(`${API_BASE}/api/invoices/${inv._id}`, { method: "DELETE", headers: auth() }); const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.error || "Couldn't delete"); say?.(`${inv.invoiceNumber} deleted`); onBack(true); }
            catch (x) { setDel({ error: x.message }); }
          }}>Delete</button></>}>
        <p className="v2c-remove-text">The cancelled invoice and its ledger entry are removed for good. This can't be undone.</p>
        {del?.error && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {del.error}</div>}
      </Sheet>
    </div>
  );
}

const IRN_REASONS = [{ k: "duplicate", l: "Duplicate" }, { k: "data_error", l: "Data entry mistake" }, { k: "order_cancelled", l: "Order cancelled" }, { k: "other", l: "Other" }];
function IrnCancelSheet({ open, inv, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { if (open) { setReason(""); setRemark(""); setErr(""); } }, [open]);
  const go = async () => {
    if (!reason) { setErr("Choose a reason."); return; }
    setBusy(true); setErr("");
    try {
      const r = await fetch(`${API_BASE}/api/irn/cancel/${inv._id}`, { method: "POST", headers: json(), body: JSON.stringify({ cancelReason: reason, cancelRemark: remark.trim() }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Couldn't cancel the IRN");
      onDone();
    } catch (x) { setErr(x.message); } finally { setBusy(false); }
  };
  return (
    <Sheet open={open} onClose={onClose} width={480} title="Cancel IRN?" subtitle={inv?.invoiceNumber}
      footer={<><button type="button" className="v2-btn" onClick={onClose}>Keep IRN</button><button type="button" className="v2-btn v2c-btn-danger" disabled={busy} onClick={go}>{busy ? "Cancelling…" : "Cancel IRN"}</button></>}>
      <div className="v2-form">
        <p className="v2c-remove-text">This is reported to the IRP and can't be reversed. The invoice itself stays active — cancel it separately if needed.</p>
        <div className="v2ac-roles" role="radiogroup" aria-label="Reason">
          {IRN_REASONS.map((x) => (
            <button key={x.k} type="button" role="radio" aria-checked={reason === x.k} className={`v2ac-role-opt${reason === x.k ? " is-on" : ""}`} onClick={() => setReason(x.k)}>
              <span className="v2ac-radio" aria-hidden="true" /><span><b>{x.l}</b></span>
            </button>))}
        </div>
        <Field label="Remark" optional><input className="v2-input" value={remark} onChange={(e) => setRemark(e.target.value)} maxLength={100} /></Field>
        {err && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {err}</div>}
      </div>
    </Sheet>
  );
}
